import { Button } from '@/shared/ui/button.tsx';
import React from 'react';

interface DownloadCSVProps {
  jsonData: Record<string, any>; // Assuming this is `response.data`
  filename?: string;
}

const DownloadCSV: React.FC<DownloadCSVProps> = ({ jsonData, filename = 'data.csv' }) => {
  const flattenObject = (obj: Record<string, any>, parentKey = '', result: Record<string, any> = {}): Record<string, any> => {
    for (const key in obj) {
      const value = obj[key];
      const fullKey = parentKey ? `${parentKey}.${key}` : key;

      if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        flattenObject(value, fullKey, result);
      } else if (Array.isArray(value)) {
        result[fullKey] = value.join('; '); // Join arrays with semicolons
      } else {
        result[fullKey] = value;
      }
    }
    return result;
  };

  const processPosts = (
    posts: any[],
    commonData: Record<string, any>
  ): Record<string, any>[] => {
    return posts.map(post => ({ ...commonData, ...flattenObject(post) }));
  };

  const generateCSV = (data: Record<string, any>) => {
    const { brand_specific_data, event_specific_data, generated_posts } = data;

    // Flatten common data fields
    const commonData = {
      ...flattenObject(brand_specific_data),
      ...flattenObject(event_specific_data),
    };

    // Process posts
    const postsData = generated_posts?.brand_specific_posts
      ? processPosts(generated_posts.brand_specific_posts, commonData)
      : [commonData];

    // Generate CSV headers
    const headers = Array.from(
      new Set(postsData.flatMap(row => Object.keys(row)))
    );

    // Generate CSV rows
    const csvRows = [
      headers.join(','), // Header row
      ...postsData.map(row =>
        headers.map(header => (row[header] ? `"${row[header]}"` : '')).join(',')
      ),
    ];

    return csvRows.join('\n');
  };

  const downloadCSV = () => {
    const csvData = generateCSV(jsonData);
    const blob = new Blob([csvData], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();

    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return <Button onClick={downloadCSV}>Download CSV</Button>;
};

export default DownloadCSV;
